import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { usePopperTooltip } from 'react-popper-tooltip'
import { Config, PopperOptions } from 'react-popper-tooltip/dist/types'
import createPortal from '../../utils/createPortal'

export interface ITooltipProps {
  content: ReactNode
  children: any
  containerTag?: string
  containerClassName?: string
  contentClassName?: string
  paddingClass?: string
  showArrow?: boolean
  placement?: Config['placement']
  offset?: Config['offset']
  interactive?: Config['interactive']
  modifiers?: PopperOptions['modifiers']
  zIndex?: string
  followCursor?: Config['followCursor']
  delayHide?: Config['delayHide']
  delayShow?: Config['delayShow']
  visible?: boolean
  open?: boolean
  onOpenChange?: Config['onVisibleChange']
}

const Tooltip = ({
  content,
  children,
  containerTag = 'span',
  containerClassName,
  contentClassName,
  paddingClass = 'px-3 py-2',
  showArrow = true,
  placement,
  offset,
  modifiers = [],
  interactive,
  followCursor,
  zIndex = 'z-50',
  delayHide = 0,
  delayShow = 0,
  visible = true,
  open,
  onOpenChange
}: ITooltipProps) => {
  const {
    getTooltipProps,
    getArrowProps,
    setTooltipRef,
    setTriggerRef,
    visible: popperVisible
  } = usePopperTooltip(
    {
      placement,
      offset,
      interactive,
      followCursor,
      delayHide,
      delayShow,
      visible: visible ? open : false,
      onVisibleChange: onOpenChange
    },
    {
      modifiers: [
        ...modifiers,
        {
          name: 'eventListeners',
          options: {
            scroll: false,
            resize: true
          }
        }
      ]
    }
  )

  const Container = containerTag as any

  return (
    <>
      <Container ref={setTriggerRef} className={containerClassName}>
        {children}
      </Container>
      {popperVisible &&
        createPortal(
          <div
            ref={setTooltipRef}
            {...getTooltipProps({
              className: classNames('tooltip-container', zIndex)
            })}
          >
            <Transition
              as="div"
              show
              appear
              enter="transition duration-100 ease-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {showArrow && (
                <div {...getArrowProps({ className: 'tooltip-arrow' })} />
              )}
              <div
                className={classNames([
                  'bg-white shadow-popup rounded-md text-oxford-gray-800 text-xs max-w-[500px]',
                  paddingClass,
                  contentClassName
                ])}
              >
                {content}
              </div>
            </Transition>
          </div>
        )}
    </>
  )
}

export default Tooltip
